import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Chip,
  Pagination,
  Divider,
  List,
  ListItem,
  Paper,
} from "@mui/material";
import { useSnackbar } from "notistack";
import axios from "../../api/axios";
import { MAIL_INGESTION_LOGS_ENDPOINT, ERROR_SNACKBAR } from "../../consts";
import CustomCircularProgress from "../../components/ui/customCircularProgress/customCircularProgress.component";
import { formatDateTime } from "../../utils";

export interface MailIngestionLog {
  id: number;
  executed_at: string;
  messages_fetched: number;
  messages_parsed: number;
  counters_created: number;
  counters_created_from_pdf: number;
  counters_created_from_counter_report: number;
  errors: unknown[];
}

interface MailIngestionLogsProps {
  refreshKey?: number;
}

const PAGE_SIZE = 3;

const formatLogError = (error: unknown) => {
  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object") {
    if ("error" in error) {
      return String(error.error);
    }

    if ("message" in error) {
      return String(error.message);
    }

    return JSON.stringify(error);
  }

  return String(error);
};

export const MailIngestionLogs = ({ refreshKey = 0 }: MailIngestionLogsProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [logs, setLogs] = useState<MailIngestionLog[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const lastHandledRefreshKey = useRef(refreshKey);

  const fetchLogs = useCallback(async (currentPage: number) => {
    setLoading(true);
    try {
      const response = await axios.get(MAIL_INGESTION_LOGS_ENDPOINT, {
        params: { page: currentPage, page_size: PAGE_SIZE },
      });
      setLogs(response.data.results ?? []);
      setCount(response.data.count ?? 0);
    } catch {
      setLogs([]);
      setCount(0);
      enqueueSnackbar(t("Failed to fetch history of counters ingestion"), ERROR_SNACKBAR);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, t]);

  useEffect(() => {
    fetchLogs(page);
  }, [fetchLogs, page]);

  useEffect(() => {
    if (refreshKey === 0 || refreshKey === lastHandledRefreshKey.current) {
      return;
    }

    lastHandledRefreshKey.current = refreshKey;

    if (page === 1) {
      fetchLogs(1);
      return;
    }

    setPage(1);
  }, [fetchLogs, page, refreshKey]);

  const totalPages = Math.ceil(count / PAGE_SIZE);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CustomCircularProgress />
      </Box>
    );
  }

  if (logs.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="text.secondary">
          {t("No history of counters ingestion available.")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <List disablePadding>
        {logs.map((log, index) => (
          <Box key={log.id}>
            <ListItem disablePadding sx={{ py: 1.5 }}>
              <Paper
                variant="outlined"
                sx={{ p: 2, width: "100%", borderRadius: "var(--defaultRadius)" }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {formatDateTime(log.executed_at)}
                  </Typography>
                  <Chip
                    label={log.errors.length > 0 ? t("With errors") : t("Success")}
                    color={log.errors.length > 0 ? "error" : "success"}
                    size="small"
                  />
                </Box>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    {t("Fetched")}: <strong>{log.messages_fetched}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("Parsed")}: <strong>{log.messages_parsed}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("Counters created")}: <strong>{log.counters_created}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("From PDFs")}: <strong>{log.counters_created_from_pdf}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("From counter reports")}:{" "}
                    <strong>{log.counters_created_from_counter_report}</strong>
                  </Typography>
                </Box>
                {log.errors.length > 0 && (
                  <Box mt={1}>
                    <Typography variant="caption" color="error">
                      {t("Errors")}:
                    </Typography>
                    {log.errors.map((err, i) => (
                      <Typography key={i} variant="caption" display="block" color="error" sx={{ ml: 1 }}>
                        {formatLogError(err)}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Paper>
            </ListItem>
            {index < logs.length - 1 && <Divider />}
          </Box>
        ))}
      </List>
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center">
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Box>
  );
};
